// frontend/src/components/legal/TermsOfService.js
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, AlertCircle, Scale } from 'lucide-react';

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-surface-page">
      {/* Header */}
      <div className="bg-surface-card border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link 
              to="/" 
              className="flex items-center text-ink-muted hover:text-ink-soft transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Home
            </Link>
            <div className="flex items-center text-ink-muted text-sm">
              <FileText className="w-4 h-4 mr-1" />
              Last Updated: January 2024
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-surface-card rounded-card shadow-elevation-1 p-8 md:p-12">
          {/* Title Section */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <Scale className="w-12 h-12 text-accent" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-ink-soft mb-4">
              Terms of Service
            </h1>
          </div>

          {/* Important Notice */}
          <div className="bg-info-tint border border-info-border rounded-card p-4 mb-8 max-w-[34rem] mx-auto">
            <div className="flex">
              <AlertCircle className="w-5 h-5 text-info mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h2 className="text-lg font-semibold text-info mb-2">
                  Important Legal Notice
                </h2>
                <p className="text-info">
                  By accessing or using The CROW platform, you agree to be bound by these Terms of Service. 
                  If you disagree with any part of these terms, you may not access our service.
                </p>
              </div>
            </div>
          </div>

          {/* Terms Content */}
          <div className="prose prose-gray max-w-[34rem] mx-auto">
            {/* Section 1 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <span className="text-accent mr-3">1.</span>
                Acceptance of Terms
              </h2>
              <p className="text-body text-ink-soft mb-4">
                These Terms of Service ("Terms") govern your use of The CROW platform, including our website, 
                mobile applications, and all related services (collectively, the "Service"). The Service is 
                owned and operated by The CROW ("Company", "we", "us", or "our").
              </p>
              <p className="text-body text-ink-soft">
                By creating an account or using the Service, you acknowledge that you have read, understood, 
                and agree to be bound by these Terms and our Privacy Policy.
              </p>
            </section>

            {/* Section 2 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <span className="text-accent mr-3">2.</span>
                Service Description
              </h2>
              <p className="text-body text-ink-soft mb-4">
                The CROW provides an automotive maintenance and management platform that includes:
              </p>
              <ul className="list-disc pl-6 mb-4 text-body text-ink-soft space-y-2">
                <li>Vehicle profile management and tracking</li>
                <li>Maintenance scheduling and recommendations</li>
                <li>Professional tools for mechanics and repair shops</li>
                <li>Customer database management and invoicing</li>
                <li>AI-powered diagnostic assistance</li>
              </ul>
              <p className="text-body text-ink-soft">
                We reserve the right to modify, suspend, or discontinue any part of the Service at any time 
                without prior notice.
              </p>
            </section>

            {/* Section 3 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <span className="text-accent mr-3">3.</span>
                User Accounts
              </h2>
              <h3 className="text-section-title text-ink mb-3">3.1 Account Registration</h3>
              <p className="text-body text-ink-soft mb-4">
                To access certain features of the Service, you must create an account. You agree to:
              </p>
              <ul className="list-disc pl-6 mb-4 text-body text-ink-soft space-y-2">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and update your information to keep it accurate</li>
                <li>Maintain the security of your password and account</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorized use</li>
              </ul>

              <h3 className="text-section-title text-ink mb-3">3.2 Age Requirements</h3>
              <p className="text-body text-ink-soft mb-4">
                You must be at least 18 years old to use the Service. By using the Service, you represent 
                and warrant that you meet this age requirement.
              </p>

              <h3 className="text-section-title text-ink mb-3">3.3 Account Termination</h3>
              <p className="text-body text-ink-soft">
                We reserve the right to suspend or terminate your account at our discretion, including for 
                violations of these Terms or any unlawful or harmful behavior.
              </p>
            </section>

            {/* Section 4 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <span className="text-accent mr-3">4.</span>
                Subscription and Payments
              </h2>
              <h3 className="text-section-title text-ink mb-3">4.1 Subscription Plans</h3>
              <p className="text-body text-ink-soft mb-4">
                The CROW offers various subscription tiers with different features and limits. Current plans include:
              </p>
              {/* Mirrors the plans offered in SubscriptionSelection.js (and the
                  backend's SUBSCRIPTION_TIERS). Update both together. */}
              <p className="text-body-strong text-ink mb-2">Car owners</p>
              <ul className="list-disc pl-6 mb-4 text-body text-ink-soft space-y-2">
                <li><strong>Free:</strong> $0 - one vehicle and basic features</li>
                <li><strong>Pro:</strong> $1.99/month or $9.99/year, or a one-time $49.99 for lifetime access - 30-day free trial</li>
              </ul>
              <p className="text-body-strong text-ink mb-2">Mechanics</p>
              <ul className="list-disc pl-6 mb-4 text-body text-ink-soft space-y-2">
                <li><strong>Free Trial:</strong> 30 days, no credit card required</li>
                <li><strong>Mechanic Pro:</strong> $29.99/month or $249.00/year</li>
                <li><strong>Mechanic Premium:</strong> $49.99/month or $399.90/year</li>
              </ul>
              <p className="text-body-strong text-ink mb-2">Fleet managers (14-day free trial)</p>
              <ul className="list-disc pl-6 mb-4 text-body text-ink-soft space-y-2">
                <li><strong>Starter:</strong> $59.99/month or $599.90/year - up to 25 vehicles</li>
                <li><strong>Growth:</strong> $99.99/month or $999.90/year - up to 50 vehicles</li>
                <li><strong>Professional:</strong> $149.99/month or $1,499.90/year - up to 100 vehicles</li>
              </ul>

              <h3 className="text-section-title text-ink mb-3">4.2 Billing</h3>
              <p className="text-body text-ink-soft mb-4">
                Subscriptions are billed in advance on a monthly or annual basis. You authorize us to charge 
                your payment method on a recurring basis. All fees are non-refundable unless otherwise stated.
              </p>

              <h3 className="text-section-title text-ink mb-3">4.3 Free Trial</h3>
              <p className="text-body text-ink-soft">
                New users receive a 30-day free trial. You will be charged automatically at the end of the 
                trial period unless you cancel before the trial expires.
              </p>
            </section>

            {/* Section 5 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <span className="text-accent mr-3">5.</span>
                Acceptable Use Policy
              </h2>
              <p className="text-body text-ink-soft mb-4">
                You agree not to use the Service to:
              </p>
              <ul className="list-disc pl-6 mb-4 text-body text-ink-soft space-y-2">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe upon intellectual property rights</li>
                <li>Transmit malware, viruses, or harmful code</li>
                <li>Engage in automated data scraping or harvesting</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Impersonate others or provide false information</li>
                <li>Use the Service for any fraudulent or deceptive purposes</li>
                <li>Resell or redistribute our data without permission</li>
                <li>Circumvent usage limits or subscription restrictions</li>
              </ul>
            </section>

            {/* Section 6 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <span className="text-accent mr-3">6.</span>
                Intellectual Property
              </h2>
              <h3 className="text-section-title text-ink mb-3">6.1 Our Property</h3>
              <p className="text-body text-ink-soft mb-4">
                The Service and its original content, features, and functionality are owned by The CROW and 
                are protected by international copyright, trademark, and other intellectual property laws.
              </p>

              <h3 className="text-section-title text-ink mb-3">6.2 User Content</h3>
              <p className="text-body text-ink-soft">
                You retain ownership of any content you submit to the Service. By submitting content, you 
                grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, and display 
                your content in connection with the Service.
              </p>
            </section>

            {/* Section 7 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <span className="text-accent mr-3">7.</span>
                Disclaimers and Limitations
              </h2>
              <h3 className="text-section-title text-ink mb-3">7.1 Service Disclaimer</h3>
              <div className="bg-warning-tint border border-warning-border rounded-card p-4 mb-4 max-w-[34rem] mx-auto">
                <p className="text-body text-ink-soft font-semibold">
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
                  EXPRESS OR IMPLIED. WE DO NOT GUARANTEE THE ACCURACY, COMPLETENESS, OR RELIABILITY 
                  OF ANY PARTS INFORMATION OR COMPATIBILITY DATA.
                </p>
              </div>

              <h3 className="text-section-title text-ink mb-3">7.2 Automotive Disclaimer</h3>
              <p className="text-body text-ink-soft mb-4">
                The information provided through the Service is for reference only. Always verify parts 
                compatibility with qualified professionals before making purchases or repairs. We are not 
                responsible for any damage resulting from incorrect parts selection or installation.
              </p>

              <h3 className="text-section-title text-ink mb-3">7.3 Limitation of Liability</h3>
              <p className="text-body text-ink-soft">
                To the maximum extent permitted by law, The CROW shall not be liable for any indirect, 
                incidental, special, consequential, or punitive damages resulting from your use or 
                inability to use the Service.
              </p>
            </section>

            {/* Section 8 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <span className="text-accent mr-3">8.</span>
                Indemnification
              </h2>
              <p className="text-body text-ink-soft">
                You agree to defend, indemnify, and hold harmless The CROW, its affiliates, and their 
                respective officers, directors, employees, and agents from any claims, liabilities, damages, 
                losses, and expenses arising from your use of the Service or violation of these Terms.
              </p>
            </section>

            {/* Section 9 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <span className="text-accent mr-3">9.</span>
                Privacy and Data Protection
              </h2>
              <p className="text-body text-ink-soft">
                Your use of the Service is also governed by our Privacy Policy. Please review our Privacy 
                Policy to understand our practices regarding your personal information and how we will treat it.
              </p>
            </section>

            {/* Section 10 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <span className="text-accent mr-3">10.</span>
                Governing Law
              </h2>
              <p className="text-body text-ink-soft">
                These Terms shall be governed by and construed in accordance with the laws of the United States 
                and the State of California, without regard to its conflict of law provisions. Any disputes 
                arising under these Terms shall be subject to the exclusive jurisdiction of the courts of 
                California.
              </p>
            </section>

            {/* Section 11 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <span className="text-accent mr-3">11.</span>
                Changes to Terms
              </h2>
              <p className="text-body text-ink-soft">
                We reserve the right to modify these Terms at any time. If we make material changes, we will 
                notify you by email or through the Service. Your continued use of the Service after changes 
                constitute acceptance of the modified Terms.
              </p>
            </section>

            {/* Section 12 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <span className="text-accent mr-3">12.</span>
                Contact Information
              </h2>
              <p className="text-body text-ink-soft mb-4">
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <div className="bg-surface-page rounded-lg p-4">
                <p className="text-body text-ink-soft">
                  <strong>The CROW Support</strong><br />
                  Email: hello@getcrowmobile.com<br />
                  Address: 8 Strathearn Ave, Unit 24, Brampton, ON L6T 4L9<br />
                  Phone: 514-617-5478
                </p>
              </div>
            </section>
          </div>

          {/* Agreement Section */}
          <div className="mt-12 pt-8 border-t border-border-default">
            <div className="text-center">
              <p className="text-ink-muted mb-4 max-w-[34rem] mx-auto">
                By using The CROW platform, you acknowledge that you have read, understood, and agree to be 
                bound by these Terms of Service.
              </p>
              <Link 
                to="/privacy" 
                className="text-accent hover:text-accent-hover font-medium"
              >
                View Privacy Policy →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;